import {useCallback, useMemo} from 'react';
import isEqual from 'react-fast-compare';

import {SchemaCollaborator} from 'auth/PermissionConstants';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import * as BenchlingUtil from 'BenchlingUtil';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AllBoxSchemasWithRegistryQuery} from 'inventory/__generated/AllBoxSchemasWithRegistryQuery';
import {AllContainerSchemasWithRegistryQuery} from 'inventory/__generated/AllContainerSchemasWithRegistryQuery';
import {AllLocationSchemasWithRegistryQuery} from 'inventory/__generated/AllLocationSchemasWithRegistryQuery';
import {AllPlateSchemasWithRegistryQuery} from 'inventory/__generated/AllPlateSchemasWithRegistryQuery';
import {LookupConfigSpec, SchemaById} from 'lookup-config/LookupConfigModalConstants';
import notifications from 'notifications';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {AllProcessTaskSchemasForSchemaConfigQuery} from 'processes/modals/utils/__generated/AllProcessTaskSchemasForSchemaConfigQuery';
import {isExecutionBlockedByTemplatePublishing} from 'processes/util/ProcessTaskExecutionUtil';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {getNewOrUpdatedSnapshotFields, haveFieldsChanged} from 'registry/field/FieldUtil';
import {AllResultSchemasQuery} from 'results/__generated/AllResultSchemasQuery';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {TagSchemasWithRegistryQuery} from 'schemas/util/__generated/TagSchemasWithRegistryQuery';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import legacyRunModal from 'ui-library/legacyRunModal';
import {withLoadingScreen} from 'util/Promises';
import {FieldNamingException} from 'warehouse/nameToSqlIdentifier';

import {ProcessTaskSchemaGraphQL} from '../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskDefinitionVersionGraphQL} from './graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  DEFAULT_OUTPUT_SCHEMA_PREFIX,
  ProcessTaskSchemaEditorContentSections,
} from './ProcessTaskSchemaEditorConstants';
import {
  cacheUpdatedTaskSchema,
  createOrUpdateSnapshotField,
  isEmptyOrWhitespace,
  UpdateProcessSchema,
  updateProcessTaskSchema,
  updateProcessTaskSchemaForTaskDefinitionVersion,
} from './ProcessTaskSchemaUtils';
import type {FieldsKey, MinimalProcessTaskSchema, ProcessTaskSchema} from './ProcessTaskSchemaUtils';
import {buildCreateRequestData, getValidationErrorMessage} from './processTaskSchemaValidation';
import RemoveOutputSchemaModal from './RemoveOutputSchemaModal';
import {useSchemaFieldsState} from './useSchemaFieldsState';
import type {SchemaFieldsState} from './useSchemaFieldsState';
import {useSchemaFormState} from './useSchemaFormState';
import type {SchemaFormState} from './useSchemaFormState';

const SAVING_TASK_SCHEMA_MESSAGE = 'Saving...';

type PlateSchemaGQL = AllPlateSchemasWithRegistryQuery['plateSchemas'][number];
type LocationSchemaGQL = AllLocationSchemasWithRegistryQuery['locationSchemas'][number];
type ContainerSchemaGQL = AllContainerSchemasWithRegistryQuery['containerSchemas'][number];
type BoxSchemaGQL = AllBoxSchemasWithRegistryQuery['boxSchemas'][number];
type StorableSchemaGQL = BoxSchemaGQL | ContainerSchemaGQL | LocationSchemaGQL | PlateSchemaGQL;
type TagSchemaGQL = TagSchemasWithRegistryQuery['tagSchemas'][number];
type TaskSchemaGQL =
  AllProcessTaskSchemasForSchemaConfigQuery['processTaskSchemasSupportingPlatformFeature'][number];
type ResultSchemaGQL = AllResultSchemasQuery['resultSchemas'][number];

export interface UseProcessTaskSchemaEditorStateArgs {
  processTaskSchema: ProcessTaskSchema | null;
  storableSchemas: Array<StorableSchemaGQL> | null;
  tagSchemas: Array<TagSchemaGQL> | null;
  taskSchemas: Array<TaskSchemaGQL> | null;
  statusLifecycles: Array<{id: string; name: string; schemaExecutionType: ProcessExecutionType}> | null;
  refetchProcessSchema?: () => void;
  beforeTaskSchemaUpdate?: () => void;
  afterTaskSchemaUpdate?: (
    newTaskSchema: ProcessTaskSchemaGraphQL,
    newTaskDefinitionVersion: ProcessTaskDefinitionVersionGraphQL | null
  ) => void;
  resultSchemas: Array<ResultSchemaGQL> | null;
  isAnyPartOfFlowchartConfigInaccessible: boolean | null;
  sectionsToHide?: Array<ProcessTaskSchemaEditorContentSections>;
  locked?: boolean;
  restrictedToExecutionType?: ProcessExecutionType;
  restrictedToOrganizationId?: string;
  createProcessTaskSchema?: (requestData: ProcessSchemaDAO.CreateProcessTaskSchemaRequest) => Promise<void>;
  /**
   * Optional set of collaborators to apply to the newly-created task schema. When provided and
   * the default create path runs (i.e. `createProcessTaskSchema` is not overridden), the editor
   * calls `updateCollaborators` with these collaborators after successful creation, before
   * navigating to the schema page. Used by the multi-step access-policies create flow.
   */
  collaboratorsToApplyAfterCreate?: Array<SchemaCollaborator> | null;
  noSectionSpacing?: boolean;
  taskDefinitionId?: string;
  isSettingsPage?: boolean;
  registryId?: string;
  stickyCreateButton?: boolean;
}

export interface ProcessTaskSchemaEditorStateResult extends SchemaFieldsState, SchemaFormState {
  // Derived
  isBeingCreated: boolean;
  isArchived: boolean;
  isLocked: boolean;
  allSchemasById: SchemaById;
  haveSchemasLoaded: boolean;
  shouldHideSectionHeaders: boolean;
  shouldHideSection: (section: ProcessTaskSchemaEditorContentSections) => boolean;
  validationErrorMessage: string | null;
  flowchartArchivedTaskSchemas: Array<{name: string} | {__typename: string}>;
  shouldShowTemplateUnpublishedBanner: boolean;
  shouldShowTemplateArchivedBanner: boolean;
  generatePrefixPreview: (label: string, prefix: string) => string;

  // Handlers
  toggleHasOutputSchemaButton: () => void;
  updateProcessTaskSchemaWithFields: (
    params: UpdateProcessSchema & {updateTables?: boolean}
  ) => Promise<void>;
  onFieldsChanged: (
    oldFields: Array<ClientSchemaField>,
    newFields: Array<ClientSchemaField>,
    fieldsKey: FieldsKey,
    setLocalFields: (fields: Array<ClientSchemaField>) => void
  ) => Promise<void>;
  handleCreateProcessTaskSchema: () => Promise<void>;
  handleDefaultResponsiblePartiesUpdate: (parties: Array<MinimalEntity>) => Promise<void>;
  handleResponsiblePartyLookupConfigUpdate: (config: LookupConfigSpec | null) => Promise<void>;
  handleDefaultCreationFolderIdUpdate: (id: string | null) => Promise<void>;
  handleDefaultEntryExecutionFolderIdUpdate: (id: string | null) => Promise<void>;
  handleTaskGroupInstructionsUpdate: (instructions: string) => Promise<void>;
}

export function useProcessTaskSchemaEditorState(
  args: UseProcessTaskSchemaEditorStateArgs
): ProcessTaskSchemaEditorStateResult {
  const {processTaskSchema} = args;
  const isBeingCreated = processTaskSchema == null;
  const isArchived = processTaskSchema?.archiveRecord != null;
  const isLocked = args.locked != null && args.locked;

  const fieldState = useSchemaFieldsState(processTaskSchema as MinimalProcessTaskSchema | null);
  const formState = useSchemaFormState({
    processTaskSchema: processTaskSchema as MinimalProcessTaskSchema | null,
    statusLifecycles: args.statusLifecycles,
    restrictedToExecutionType: args.restrictedToExecutionType,
    restrictedToOrganizationId: args.restrictedToOrganizationId,
    locked: args.locked,
  });

  // Compose setStateOnUpdate from both sub-hooks
  const setStateOnUpdate = useCallback(
    (params: UpdateProcessSchema & {updateTables?: boolean}): void => {
      fieldState.applyFieldUpdates(params);
      formState.applyFormUpdates(params);
    },
    [fieldState, formState]
  );

  // --- Derived state ---

  const {tagSchemas, taskSchemas, storableSchemas, resultSchemas} = args;
  const haveSchemasLoaded = [tagSchemas, taskSchemas, storableSchemas, resultSchemas].every(
    (arr) => arr != null
  );
  const allSchemasById = useMemo(() => {
    const schemasById = [...(tagSchemas ?? []), ...(storableSchemas ?? []), ...(resultSchemas ?? [])].reduce(
      (acc, schema) => {
        acc[schema.id] = {
          ...schema,
          fields: schema.fieldsJson as Array<SchemaField>,
          allFields: schema.allFieldsJson as Array<SchemaField>,
        } as unknown as Schema;
        return acc;
      },
      {} as SchemaById
    );

    for (const taskSchema of taskSchemas ?? []) {
      schemasById[taskSchema.id] = taskSchema as unknown as Schema;
      if (taskSchema.outputSchema != null) {
        schemasById[taskSchema.outputSchema.id] = taskSchema.outputSchema as unknown as Schema;
      }
    }
    if (processTaskSchema != null) {
      schemasById[processTaskSchema.id] = processTaskSchema;
      if (processTaskSchema.outputSchema != null) {
        schemasById[processTaskSchema.outputSchema.id] = processTaskSchema.outputSchema;
      }
    }
    return schemasById;
  }, [tagSchemas, storableSchemas, resultSchemas, taskSchemas, processTaskSchema]);

  const shouldHideSectionHeaders = useMemo(
    () =>
      args.sectionsToHide != null &&
      Object.values(ProcessTaskSchemaEditorContentSections).length - (args.sectionsToHide.length ?? 0) <= 1,
    [args]
  );

  const shouldHideSection = useCallback(
    (section: ProcessTaskSchemaEditorContentSections): boolean =>
      args.sectionsToHide != null && args.sectionsToHide.includes(section),
    [args]
  );

  const flowchartArchivedTaskSchemas = useMemo(() => {
    const fcv = processTaskSchema?.flowchartConfig?.currentFlowchartConfigVersion;
    if (fcv == null || isInaccessible(fcv)) {
      return [];
    }
    return fcv.availableSchemas.filter(
      (schema) => !isInaccessibleInfo(schema) && schema.archiveRecord != null
    );
  }, [processTaskSchema]);

  const shouldShowTemplateUnpublishedBanner = useMemo(() => {
    if (isBeingCreated || processTaskSchema == null) {
      return false;
    }
    return isExecutionBlockedByTemplatePublishing(processTaskSchema);
  }, [isBeingCreated, processTaskSchema]);

  const shouldShowTemplateArchivedBanner = useMemo(() => {
    if (isBeingCreated) {
      return false;
    }
    return flowchartArchivedTaskSchemas.length > 0;
  }, [isBeingCreated, flowchartArchivedTaskSchemas]);

  const generatePrefixPreview = useCallback(
    (label: string, prefix: string): string =>
      isEmptyOrWhitespace(formState.taskGroupPrefix)
        ? ''
        : `${label}: ${formState.taskGroupPrefix}1-${prefix}1`,
    [formState]
  );

  const validationErrorMessage = useMemo(() => {
    if (formState.schemaOwner == null) {
      return 'Enter an owner before creating this schema.';
    }
    return getValidationErrorMessage({
      processTaskSchemaName: formState.processTaskSchemaName,
      processTaskSchemaSqlIdentifier: formState.processTaskSchemaSqlIdentifier,
      taskGroupPrefix: formState.taskGroupPrefix,
      taskSchemaPrefix: formState.taskSchemaPrefix,
      executionType: formState.executionType,
      canSetAssigneeOnTaskCreation: formState.canSetAssigneeOnTaskCreation,
      taskFields: fieldState.taskFields,
      organizationId: formState.schemaOwner.id,
      defaultResponsibleParties: formState.defaultResponsibleParties,
      statusLifecycleId: formState.statusLifecycle?.id ?? '',
      defaultCreationFolderId: formState.defaultCreationFolderId,
      taskGroupInstructions: formState.taskGroupInstructions,
      isPropagateWatchersEnabled: formState.isPropagateWatchersEnabled,
      hasOutputSchema: fieldState.hasOutputSchema,
      outputSchemaPrefix: fieldState.outputSchemaPrefix,
      outputFields: fieldState.outputFields,
      executionTemplate: formState.executionTemplate,
      defaultEntryExecutionFolderId: formState.defaultEntryExecutionFolderId,
      canOnlyBePartOfFlowchart: formState.canOnlyBePartOfFlowchart,
      autopopulateDirectOutputsFromTasks: fieldState.autopopulateDirectOutputsFromTasks,
    });
  }, [formState, fieldState]);

  // --- Handlers ---

  const updateProcessTaskSchemaWithFields = useCallback(
    async (params: UpdateProcessSchema & {updateTables?: boolean}): Promise<void> => {
      args.beforeTaskSchemaUpdate?.();
      if (args.taskDefinitionId != null) {
        await updateProcessTaskSchemaForTaskDefinitionVersion(
          params,
          args.taskDefinitionId,
          setStateOnUpdate,
          formState.setErrors,
          processTaskSchema,
          fieldState.hasOutputSchema,
          args.afterTaskSchemaUpdate
        );
      } else {
        await updateProcessTaskSchema(
          params,
          setStateOnUpdate,
          formState.setErrors,
          processTaskSchema,
          fieldState.hasOutputSchema,
          args.afterTaskSchemaUpdate
            ? (taskSchema) => args.afterTaskSchemaUpdate?.(taskSchema, null)
            : undefined
        );
      }
    },
    [args, processTaskSchema, fieldState, setStateOnUpdate, formState]
  );

  const onFieldsChanged = useCallback(
    async (
      oldFields: Array<ClientSchemaField>,
      newFields: Array<ClientSchemaField>,
      fieldsKey: FieldsKey,
      setLocalFields: (fields: Array<ClientSchemaField>) => void
    ): Promise<void> => {
      let fieldsChanged;
      try {
        fieldsChanged = haveFieldsChanged(oldFields, newFields);
      } catch (e) {
        if (e instanceof FieldNamingException) {
          notifications.error(e.message);
          return;
        } else {
          throw e;
        }
      }

      if (!fieldsChanged) {
        return;
      }

      if (!isBeingCreated) {
        const newOrUpdatedSnapshotFields = getNewOrUpdatedSnapshotFields(oldFields, newFields);
        if (newOrUpdatedSnapshotFields.length > 0) {
          await createOrUpdateSnapshotField(
            newOrUpdatedSnapshotFields,
            newFields,
            fieldsKey,
            formState.setErrors,
            setStateOnUpdate,
            args.refetchProcessSchema,
            processTaskSchema
          );
        } else if (args.taskDefinitionId != null) {
          await updateProcessTaskSchemaForTaskDefinitionVersion(
            {[fieldsKey]: newFields},
            args.taskDefinitionId,
            setStateOnUpdate,
            formState.setErrors,
            processTaskSchema,
            fieldState.hasOutputSchema,
            args.afterTaskSchemaUpdate
          );
        } else {
          await updateProcessTaskSchema(
            {[fieldsKey]: newFields},
            setStateOnUpdate,
            formState.setErrors,
            processTaskSchema,
            fieldState.hasOutputSchema,
            (taskSchema) => args.afterTaskSchemaUpdate?.(taskSchema, null)
          );
        }
      } else {
        setLocalFields(newFields);
      }
    },
    [isBeingCreated, processTaskSchema, fieldState, setStateOnUpdate, formState, args]
  );

  const toggleHasOutputSchemaButton = useCallback((): void => {
    if (fieldState.hasOutputSchema) {
      legacyRunModal(RemoveOutputSchemaModal, {
        onRequestSubmit: () => {
          fieldState.setHasOutputSchema(false);
          fieldState.setOutputSchemaPrefix(DEFAULT_OUTPUT_SCHEMA_PREFIX);
          fieldState.setOutputFields([]);
        },
      });
    } else {
      fieldState.setHasOutputSchema(true);
    }
  }, [fieldState]);

  const handleDefaultResponsiblePartiesUpdate = useCallback(
    async (newDefaultResponsibleParties: Array<MinimalEntity>): Promise<void> => {
      if (isEqual(newDefaultResponsibleParties, formState.defaultResponsibleParties)) {
        return;
      }
      if (!isBeingCreated) {
        await updateProcessTaskSchemaWithFields({
          defaultResponsibleParties: newDefaultResponsibleParties,
        });
      } else {
        formState.applyFormUpdates({defaultResponsibleParties: newDefaultResponsibleParties});
      }
    },
    [formState, isBeingCreated, updateProcessTaskSchemaWithFields]
  );

  const handleResponsiblePartyLookupConfigUpdate = useCallback(
    async (newResponsiblePartyLookupConfig: LookupConfigSpec | null): Promise<void> => {
      if (isEqual(newResponsiblePartyLookupConfig, formState.responsiblePartyLookupConfig)) {
        return;
      }
      if (!isBeingCreated) {
        await updateProcessTaskSchemaWithFields({
          responsiblePartyLookupConfig: newResponsiblePartyLookupConfig,
        });
      } else {
        formState.applyFormUpdates({responsiblePartyLookupConfig: newResponsiblePartyLookupConfig});
      }
    },
    [formState, isBeingCreated, updateProcessTaskSchemaWithFields]
  );

  const handleDefaultCreationFolderIdUpdate = useCallback(
    async (newDefaultCreationFolderId: string | null): Promise<void> => {
      if (newDefaultCreationFolderId === formState.defaultCreationFolderId) {
        return;
      }
      if (!isBeingCreated) {
        await updateProcessTaskSchemaWithFields({
          defaultCreationFolderId: newDefaultCreationFolderId,
        });
      } else {
        formState.applyFormUpdates({defaultCreationFolderId: newDefaultCreationFolderId});
      }
    },
    [formState, isBeingCreated, updateProcessTaskSchemaWithFields]
  );

  const handleDefaultEntryExecutionFolderIdUpdate = useCallback(
    async (newDefaultEntryExecutionFolderId: string | null): Promise<void> => {
      if (newDefaultEntryExecutionFolderId === formState.defaultEntryExecutionFolderId) {
        return;
      }
      if (!isBeingCreated) {
        await updateProcessTaskSchemaWithFields({
          defaultEntryExecutionFolderId: newDefaultEntryExecutionFolderId,
        });
      } else {
        formState.applyFormUpdates({defaultEntryExecutionFolderId: newDefaultEntryExecutionFolderId});
      }
    },
    [formState, isBeingCreated, updateProcessTaskSchemaWithFields]
  );

  const handleTaskGroupInstructionsUpdate = useCallback(
    async (newTaskGroupInstructions: string): Promise<void> => {
      if (newTaskGroupInstructions === formState.taskGroupInstructions) {
        return;
      }
      if (!isBeingCreated) {
        await updateProcessTaskSchemaWithFields({
          taskGroupInstructions: newTaskGroupInstructions,
        });
      } else {
        formState.applyFormUpdates({taskGroupInstructions: newTaskGroupInstructions});
      }
    },
    [formState, isBeingCreated, updateProcessTaskSchemaWithFields]
  );

  const handleCreateProcessTaskSchema = useCallback(async (): Promise<void> => {
    formState.setErrors({});
    if (!formState.areDefaultResponsiblePartiesValid) {
      formState.setShowDefaultResponsiblePartiesError(!formState.areDefaultResponsiblePartiesValid);
      return;
    }
    if (formState.statusLifecycle == null) {
      notifications.error('Status lifecycles are still loading. Please wait before creating this schema.');
      return;
    }
    const requestData = buildCreateRequestData({
      processTaskSchemaName: formState.processTaskSchemaName,
      processTaskSchemaSqlIdentifier: formState.processTaskSchemaSqlIdentifier,
      taskGroupPrefix: formState.taskGroupPrefix,
      taskSchemaPrefix: formState.taskSchemaPrefix,
      executionType: formState.executionType,
      canSetAssigneeOnTaskCreation: formState.canSetAssigneeOnTaskCreation,
      taskFields: fieldState.taskFields,
      organizationId: formState.schemaOwner!.id,
      defaultResponsibleParties: formState.defaultResponsibleParties,
      statusLifecycleId: formState.statusLifecycle.id,
      defaultCreationFolderId: formState.defaultCreationFolderId,
      taskGroupInstructions: formState.taskGroupInstructions,
      isPropagateWatchersEnabled: formState.isPropagateWatchersEnabled,
      hasOutputSchema: fieldState.hasOutputSchema,
      outputSchemaPrefix: fieldState.outputSchemaPrefix,
      outputFields: fieldState.outputFields,
      executionTemplate: formState.executionTemplate,
      defaultEntryExecutionFolderId: formState.defaultEntryExecutionFolderId,
      canOnlyBePartOfFlowchart: formState.canOnlyBePartOfFlowchart,
      autopopulateDirectOutputsFromTasks: fieldState.autopopulateDirectOutputsFromTasks,
    });
    try {
      if (args.createProcessTaskSchema != null) {
        await args.createProcessTaskSchema(requestData);
      } else {
        const createdTaskSchema = await withLoadingScreen(
          ProcessSchemaDAO.createProcessTaskSchema(requestData),
          SAVING_TASK_SCHEMA_MESSAGE
        );
        cacheUpdatedTaskSchema(createdTaskSchema);
        const collaboratorsToApply = args.collaboratorsToApplyAfterCreate;
        if (collaboratorsToApply != null && collaboratorsToApply.length > 0) {
          await ProcessSchemaDAO.updateCollaborators(createdTaskSchema.id, {
            collaborators: collaboratorsToApply,
          }).catch((e) => {
            notifications.notifyError(e);
          });
        }
        BenchlingUtil.navigate(null, ProcessSchemaDAO.processTaskSchemaURL(createdTaskSchema.id));
      }
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      if (e.responseJSON) {
        formState.setErrors(e.responseJSON.errors ?? {});
      }
      notifications.notifyError(e);
    }
  }, [args, formState, fieldState]);

  return {
    // Spread sub-hook state
    ...fieldState,
    ...formState,

    // Derived
    isBeingCreated,
    isArchived,
    isLocked,
    allSchemasById,
    haveSchemasLoaded,
    shouldHideSectionHeaders,
    shouldHideSection,
    validationErrorMessage,
    flowchartArchivedTaskSchemas,
    shouldShowTemplateUnpublishedBanner,
    shouldShowTemplateArchivedBanner,
    generatePrefixPreview,

    // Handlers
    toggleHasOutputSchemaButton,
    updateProcessTaskSchemaWithFields,
    onFieldsChanged,
    handleCreateProcessTaskSchema,
    handleDefaultResponsiblePartiesUpdate,
    handleResponsiblePartyLookupConfigUpdate,
    handleDefaultCreationFolderIdUpdate,
    handleDefaultEntryExecutionFolderIdUpdate,
    handleTaskGroupInstructionsUpdate,
  };
}
